"use client"

import { useMemo } from "react"

import {
  WorkspaceAccessModule,
  WorkspaceAccessPermission,
} from "@/features/auth/model/auth-types"
import {
  resolveTenantAuthorizationModuleAction,
} from "@/features/auth/tenant-authorization/lib/tenant-authorization-registry"
import {
  TenantAuthorizationDecisionStatus,
} from "@/features/auth/tenant-authorization/model/tenant-authorization.types"
import { useTenantAuthorization } from "@/features/auth/tenant-authorization/hooks/use-tenant-authorization"

type ModulePermissionResult = {
  canView: boolean
  canEdit: boolean
  isParish: boolean
  isGlobal: boolean
}

/**
 * Scope a module permission is resolved in:
 * - `"branch"` (default): the active-route branch (via its slug).
 * - `"parish"`: the real system branch represented by the visible parish alias.
 */
export type ModulePermissionScope = "branch" | "parish"

export function useModulePermission(
  module: WorkspaceAccessModule,
  options?: { scope?: ModulePermissionScope; includeEdit?: boolean },
): ModulePermissionResult {
  const scope = options?.scope ?? "branch"
  const authorization = useTenantAuthorization()

  return useMemo(() => {
    const requestedBranchSlug = scope === "parish" ? "parish" : undefined
    const view = authorization.routeDecision(
      resolveTenantAuthorizationModuleAction(module, WorkspaceAccessPermission.VIEW),
      requestedBranchSlug,
    )
    const edit = options?.includeEdit === false
      ? null
      : authorization.routeDecision(
          resolveTenantAuthorizationModuleAction(module, WorkspaceAccessPermission.EDIT),
          requestedBranchSlug,
        )
    const canView = view.status === TenantAuthorizationDecisionStatus.Allowed
    const canEdit = edit?.status === TenantAuthorizationDecisionStatus.Allowed
    return { canView, canEdit, isParish: scope === "parish" && canView, isGlobal: scope === "parish" && canView }
  }, [authorization, module, options?.includeEdit, scope])
}
